import { getConfig, klinikenApi } from "@/api";
import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Oversikt = {
    id: string;
    patient?: string;
    status?: string;
    [key: string]: unknown;
};

type State = BaseState & {
    oversikter: Map<Oversikt["id"], Oversikt>;
};

type Getters = {
    oversiktById: (data: { id?: Oversikt["id"] }) => Oversikt | null;
};

const getDefaultState: () => State = () => ({
    oversikter: new Map(),
    ...cloneDeep(baseState),
});

const moduleName = "oversikter";
const baseUrl = "operation/oversikt/";
// Listan lagras i ett enda subset, eftersom varje hämtning ersätter den sida som visas
const SUBSET_NAME = "default";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        /**
         * Läs ut den hämtade sidan. Lyssna på denna i en computed i komponenten.
         */
        oversiktSubset: (state: State) => (props: Omit<SubsetGetterProps<State>, "state" | "collectionName">) =>
            subsetGetter<State>({
                ...props,
                state,
                subsetName: SUBSET_NAME,
                collectionName: moduleName,
            }),
        /**
         * Hämta ett enskilt objekt ur store. Vill du lyssna på förändringar på en enskild rad i en computed är det
         * den här du vill använda. Behöver du hämta från BE när objektet saknas: använd actionen getOversiktById.
         */
        oversiktById:
            (state: State) =>
            ({ id }: { id?: Oversikt["id"] }) => {
                if (!id) {
                    return null;
                }

                return state.oversikter.get(id) ?? null;
            },
    },
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());
                resetDelayedResetTimeout({ moduleName });
            },
        }),
        /**
         * Ersätter innehållet i listan med den hämtade sidan. resetSubset gör att bara den sida som visas
         * finns i subsetet, i den ordning BE gav den.
         */
        setOversikter: (
            state: State,
            {
                offset,
                total,
                oversikter = [],
            }: {
                offset: number;
                oversikter: Oversikt[];
                total?: number;
            }
        ) =>
            subsetSetter<State, Oversikt>({
                offset,
                state,
                total,
                resetSubset: true,
                subsetName: SUBSET_NAME,
                collectionName: moduleName,
                items: oversikter,
            }),
        /**
         * Skriver om innehållet i redan hämtade rader och lämnar subsets orörda.
         *
         * Översikten sorteras och filtreras av BE (pågående först, genomförda och signerade sist), så
         * vilka rader som ingår och i vilken ordning avgörs av hämtningen. Operationsöversikten hämtar om
         * med forceRefresh vid varje filterklick och sidbyte.
         */
        updateOversikter: (state: State, { oversikter = [] }: { oversikter: Oversikt[] }) => {
            // Ny Map krävs för reaktivitet i Vue 2, precis som i subsetSetter
            state.oversikter = new Map([
                ...state.oversikter.entries(),
                ...oversikter.map((oversikt): [Oversikt["id"], Oversikt] => [oversikt.id, oversikt]),
            ]);
        },
    },
    actions: {
        ...baseActions({ moduleName }),
        /**
         * Hämta en sida med rader till översikten. Returnerar totalantalet träffar.
         *
         * Hämtningen görs alltid mot BE. Ordningen (pågående först, genomförda och signerade sist) och
         * urvalet avgörs där, medan rader som ändras under sessionen bara får sitt innehåll utbytt i store.
         */
        fetchOversikter: async (
            { commit, state }: BaseActionsProps<State>,
            {
                limit = 50,
                page = 1,
                filterParams = {},
                config = {},
            }: {
                limit?: number;
                page?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
            }
        ) => {
            try {
                const { results, total, nextPage, offset } = await fetchItems<State, Oversikt>({
                    filterParams,
                    state,
                    limit,
                    page,
                    config,
                    forceRefresh: true,
                    collectionName: moduleName,
                    apiUrl: baseUrl,
                });

                if (!results) {
                    return { total, nextPage };
                }

                commit("setOversikter", {
                    total,
                    offset,
                    oversikter: results,
                });

                return { results, total, nextPage };
            } catch (error) {
                console.error(error);
                throw error;
            }
        },
        /**
         * Hämta ett enskilt objekt via id. Returnerar objektet ur store om det redan finns (om inte forceRefresh),
         * annars hämtas det från BE och skrivs om i store.
         */
        getOversiktById: async (
            { getters, commit }: BaseActionsProps<State> & { getters: Getters },
            {
                id,
                forceRefresh,
            }: {
                id: Oversikt["id"];
                forceRefresh?: boolean;
            }
        ) => {
            const found = getters.oversiktById({ id });

            if (!forceRefresh && found) {
                return found;
            }

            const { data } = await klinikenApi.get<Oversikt>(`${baseUrl}${id}/`, getConfig());

            commit("updateOversikter", { oversikter: [data] });

            return data;
        },
        /**
         * Tar emot ett färdighämtat oversikt-objekt och skriver om motsvarande rad i store. Anropas från
         * Operationsjournal efter sparande.
         */
        updateOversikt: ({ commit }: BaseActionsProps<State>, oversikt: Oversikt) => {
            if (!oversikt?.id) return;
            commit("updateOversikter", { oversikter: [oversikt] });
        },
    },
};
